import { useState } from "react";
import type { EnrichedParticipant } from "../../types/participants";
import { supabase } from "../../lib/supabaseClient";
import ParticipantSelector from "./ParticipantSelector";

interface Props {
  tripId: string;
  participants: EnrichedParticipant[];
  onSaved: () => void;
  onClose: () => void;
}

export default function NewExpenseForm({
  tripId,
  participants,
  onSaved,
  onClose,
}: Props) {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("Sonstiges");
  const [amount, setAmount] = useState("");
  const [payerId, setPayerId] = useState("");
  const [selectedParticipants, setSelectedParticipants] = useState<string[]>(
    []
  );
  const [distributeToAll, setDistributeToAll] = useState(true);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const parsedAmount = parseFloat(amount);
    if (!title || isNaN(parsedAmount) || parsedAmount <= 0) return;

    const involvedUsers = distributeToAll
      ? participants.filter((p) => !p.is_blocked).map((p) => p.user_id)
      : selectedParticipants;

    if (involvedUsers.length === 0) return;

    setSaving(true);

    const { data, error } = await supabase
      .from("trip_expenses")
      .insert({
        trip_id: tripId,
        title,
        amount: parsedAmount,
        category,
        payer_id: payerId === "reisekasse" ? null : payerId,
      })
      .select()
      .single();

    if (error || !data) {
      console.error("❌ Fehler beim Speichern:", error?.message);
      setSaving(false);
      return;
    }

    const share = parseFloat((parsedAmount / involvedUsers.length).toFixed(2));

    await supabase.from("trip_expense_shares").insert(
      involvedUsers.map((userId) => ({
        expense_id: data.id,
        user_id: userId,
        amount: share,
        is_paid: false,
      }))
    );

    setSaving(false);
    onSaved();
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="bg-[#161b27] rounded-2xl border border-[#2a3347] p-6 w-full max-w-md relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-4 text-slate-600 hover:text-slate-300 text-xl transition-colors"
          aria-label="Modal schließen"
        >
          &times;
        </button>

        <h2 className="text-sm font-semibold text-slate-100 mb-5">Neue Ausgabe erfassen</h2>

        <input
          type="text"
          placeholder="Titel"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full bg-[#1e2639] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-2 text-sm mb-3 focus:outline-none focus:border-sky-500/50 placeholder:text-slate-600"
        />

        <select
          className="w-full bg-[#1e2639] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-2 text-sm mb-3 focus:outline-none focus:border-sky-500/50"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
        >
          <option value="Reisen">Reisen</option>
          <option value="Übernachtung">Übernachtung</option>
          <option value="Verpflegung">Verpflegung</option>
          <option value="Aktivitäten">Aktivitäten</option>
          <option value="Workshops">Workshops</option>
          <option value="Sonstiges">Sonstiges</option>
        </select>

        <input
          type="number"
          step="0.01"
          min="0"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="w-full bg-[#1e2639] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-2 text-sm mb-3 focus:outline-none focus:border-sky-500/50 placeholder:text-slate-600"
          placeholder="Betrag"
        />

        <select
          className="w-full bg-[#1e2639] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-2 text-sm mb-4 focus:outline-none focus:border-sky-500/50"
          value={payerId}
          onChange={(e) => setPayerId(e.target.value)}
        >
          <option value="">– Wer hat bezahlt? –</option>
          <option value="reisekasse">💼 Reisekasse</option>
          {participants.map((p) => {
            const name =
              p.first_name || p.last_name
                ? `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim()
                : p.email;
            return (
              <option key={p.user_id} value={p.user_id}>
                {name}
              </option>
            );
          })}
        </select>

        <ParticipantSelector
          participants={participants}
          selected={selectedParticipants}
          setSelected={setSelectedParticipants}
          distributeToAll={distributeToAll}
          setDistributeToAll={setDistributeToAll}
        />

        <div className="flex justify-end gap-2 mt-5">
          <button
            onClick={onClose}
            className="text-xs px-3 py-1.5 rounded-full bg-[#1e2639] text-slate-400 hover:text-slate-200 transition-colors"
          >
            Abbrechen
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="text-xs px-3 py-1.5 rounded-full bg-sky-600 text-white hover:bg-sky-500 transition-colors"
          >
            {saving ? "Speichern…" : "Speichern"}
          </button>
        </div>
      </div>
    </div>
  );
}
